import { History } from "lucide-react";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { Button } from "@/shared/components/form/Button";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import useTranslations from "@/shared/lang/useTranslations";
import type { PublicAccountItem } from "../types/public-accounts-api.types";
import { PublicAccountPaymentMethod } from "../types/public-accounts-api.types";

type SelectOption = { label: string; value: string };

interface AccountsWalletsTrackingTableProps {
  rows: PublicAccountItem[];
  totalItems: number;
  itemsPerPage: number;
  paginationOptions: SelectOption[];
  onItemsPerPageChange: (value: string) => void;
  onViewDetail: (accountId: string) => void;
}

export const AccountsWalletsTrackingTable = ({
  rows,
  totalItems,
  itemsPerPage,
  paginationOptions,
  onItemsPerPageChange,
  onViewDetail,
}: AccountsWalletsTrackingTableProps) => {
  const { t } = useTranslations("accountsWallets");
  const showingText =
    rows.length === 1
      ? t("tracking_table_showing_one", { total: totalItems })
      : t("tracking_table_showing_many", { count: rows.length, total: totalItems });

  return (
    <>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {showingText}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">{t("tracking_table_show_label")}</span>
          <InputSelect<SelectOption>
            options={paginationOptions}
            value={
              paginationOptions.find((opt) => opt.value === String(itemsPerPage)) ?? null
            }
            onChange={(selected) => {
              const value =
                Array.isArray(selected) ? selected[0]?.value : selected?.value;
              onItemsPerPageChange(value ?? "25");
            }}
            maxWidth="max-w-[120px]"
            small
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("tracking_table_th_alias")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("tracking_table_th_currency")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("tracking_table_th_type")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("tracking_table_th_created")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("tracking_table_th_actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-800">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  {t("tracking_table_empty")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                    {row.internal_alias?.trim() || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {row.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {row.payment_method === PublicAccountPaymentMethod.CRYPTO_WALLET
                      ? t("tracking_table_type_wallet")
                      : t("tracking_table_type_bank")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDateTime(row.created_at, { includeTime: true })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Button
                      variant="outline"
                      onClick={() => onViewDetail(row.id)}
                      type="button"
                      className="flex items-center gap-2"
                    >
                      <History className="h-4 w-4" />
                      {t("tracking_table_button_view")}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
